import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AdminOnly from './AdminOnly.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';
import Toast from './Toast.tsx';
import ProviderFormDialog from './ProviderFormDialog.tsx';
import CredentialsMatrix from './CredentialsMatrix.tsx';

// Providers page: one table — the credentials matrix — narrowed by the search bar above it. A provider's
// name in that table opens their card in the edit dialog, and "Create Provider" opens the same dialog
// empty, so the long form stays out of the way until someone asks for it. (Replaces the former "Employee
// Cards" and "Credentials" tabs, and the roster table that used to sit above the matrix.)

type DialogState = { mode: 'create' } | { mode: 'edit'; employee: any } | null;

const ProvidersView: React.FC = () => {
  const location = useLocation();

  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<DialogState>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const credentialsRef = useRef<HTMLDivElement>(null);
  // Stable, so the Toast's auto-dismiss timer isn't restarted by every re-render.
  const dismissToast = useCallback(() => setToast(null), []);

  // Old /credentials links land here as /providers#credentials — bring that section into view.
  useEffect(() => {
    if (location.hash === '#credentials' && typeof credentialsRef.current?.scrollIntoView === 'function') {
      credentialsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  return (
    <>
      {toast && (
        <Toast message={toast.text} type={toast.type} onClose={dismissToast} />
      )}

      <div className="card" id="credentials" ref={credentialsRef} aria-labelledby="providers-heading">
        <h1 id="providers-heading">Providers</h1>
        <ReadOnlyBanner />

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '20px' }}>Credentials</h2>
          <div style={{ flex: 1 }} />
          <input
            type="search"
            className="form-control"
            aria-label="Search providers by name"
            placeholder="🔍 Search providers…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ maxWidth: '280px' }}
          />
          <AdminOnly>
            <button type="button" className="btn btn-primary" onClick={() => setDialog({ mode: 'create' })}>
              + Create Provider
            </button>
          </AdminOnly>
        </div>

        <CredentialsMatrix nameFilter={search} onEditProvider={employee => setDialog({ mode: 'edit', employee })} />
      </div>

      <ProviderFormDialog
        open={dialog !== null}
        employee={dialog?.mode === 'edit' ? dialog.employee : null}
        onClose={() => setDialog(null)}
        onSaved={message => setToast({ type: 'success', text: message })}
      />
    </>
  );
};

export default ProvidersView;
